import React, { useEffect, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { settings } from '../../../config';
import mapMarker from '../../../assets/img/icons/map-marker.png';

let tiandituScriptPromise = null;

function loadTiandituScript(tk) {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.T && window.T.Map) return Promise.resolve();
  if (!tk) return Promise.reject(new Error('missing tianditu token'));

  if (!tiandituScriptPromise) {
    tiandituScriptPromise = new Promise((resolve, reject) => {
      const finish = () => {
        if (window.T && window.T.Map) resolve();
        else reject(new Error('Tianditu API unavailable'));
      };

      const existing = document.querySelector('script[data-tianditu-api="4.0"]');
      if (existing) {
        if (window.T && window.T.Map) {
          finish();
          return;
        }
        existing.addEventListener('load', finish);
        existing.addEventListener('error', () => reject(new Error('Tianditu load error')));
        return;
      }

      const script = document.createElement('script');
      script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${encodeURIComponent(tk)}`;
      script.async = true;
      script.dataset.tiandituApi = '4.0';
      script.onload = finish;
      script.onerror = () => reject(new Error('Tianditu script failed'));
      document.body.appendChild(script);
    });
  }
  return tiandituScriptPromise;
}

function escHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/"/g, '&quot;');
}

/**
 * 天地图多电站展示（与 CustomizeMapBox 相同的 GeoJSON Feature 数组约定）
 */
const CustomizeTiandituMap = ({ Latitude, Longitude, Zoom, Geojson, t }) => {
  const mapDomIdRef = useRef(
    `tianditu-microgrid-${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`
  );
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  const featuresJson = JSON.stringify(Geojson || []);

  useEffect(() => {
    const tk = settings.tiandituToken;
    if (!tk || !containerRef.current) return undefined;

    let cancelled = false;

    loadTiandituScript(tk)
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const T = window.T;
        if (!T || !T.Map) return;

        if (mapRef.current) {
          try {
            mapRef.current = null;
          } catch (e) {
            /* ignore */
          }
        }
        containerRef.current.innerHTML = '';

        const map = new T.Map(mapDomIdRef.current);
        map.enableScrollWheelZoom();
        map.enableDoubleClickZoom();
        map.enableDrag();
        mapRef.current = map;

        const features = JSON.parse(featuresJson);
        const lngLats = [];

        const icon = new T.Icon({
          iconUrl: mapMarker,
          iconSize: new T.Point(32, 32),
          iconAnchor: new T.Point(16, 32)
        });

        features.forEach(f => {
          const coords = f.geometry && f.geometry.coordinates;
          if (!coords || coords.length < 2) return;
          const lng = Number(coords[0]);
          const lat = Number(coords[1]);
          if (Number.isNaN(lng) || Number.isNaN(lat)) return;

          const lngLat = new T.LngLat(lng, lat);
          lngLats.push(lngLat);
          const marker = new T.Marker(lngLat, { icon });
          const p = f.properties || {};
          const url = p.url || '/singlemicrogrid/details';
          const uuid = p.uuid != null ? String(p.uuid) : '';
          const title = p.title != null ? String(p.title) : '';
          const description = p.description != null ? String(p.description) : '';

          marker.addEventListener('click', () => {
            const infoWin = new T.InfoWindow();
            const href = `${url}?uuid=${encodeURIComponent(uuid)}`;
            infoWin.setContent(
              `<div style="min-width:160px;padding:8px;font-family:sans-serif;">
                <a target="_blank" rel="noopener noreferrer" href="${href}">${escHtml(title)}</a>
                ${
                  description
                    ? `<div style="margin-top:6px;font-size:12px;color:#333;">${escHtml(description)}</div>`
                    : ''
                }
              </div>`
            );
            if (typeof infoWin.setOffset === 'function') {
              infoWin.setOffset(new T.Point(0, -32));
            }
            marker.openInfoWindow(infoWin);
          });

          map.addOverLay(marker);
        });

        const z = Zoom != null ? Zoom : 4;
        if (lngLats.length === 0) {
          const centerLng =
            Longitude != null && Longitude !== '' ? Number(Longitude) : 105;
          const centerLat = Latitude != null && Latitude !== '' ? Number(Latitude) : 35;
          map.centerAndZoom(
            new T.LngLat(
              Number.isNaN(centerLng) ? 105 : centerLng,
              Number.isNaN(centerLat) ? 35 : centerLat
            ),
            z
          );
        } else if (lngLats.length === 1) {
          map.centerAndZoom(lngLats[0], Math.max(z, 10));
        } else if (typeof map.setViewport === 'function') {
          map.setViewport(lngLats);
        } else {
          map.centerAndZoom(lngLats[0], z);
        }
      })
      .catch(() => {
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = `<div class="p-3 text-center text-muted small">${escHtml(
          t('Tianditu map load failed', {
            defaultValue: '地图加载失败，请检查 config.js 中的 tiandituToken 与网络。'
          })
        )}</div>`;
      });

    return () => {
      cancelled = true;
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      mapRef.current = null;
    };
  }, [Latitude, Longitude, Zoom, featuresJson, t]);

  if (!settings.tiandituToken) {
    return (
      <div className="d-flex align-items-center justify-content-center p-4 text-muted small" style={{ minHeight: 360 }}>
        {t('Set tiandituToken in config', {
          defaultValue: '请在 config.js 的 settings 中配置 tiandituToken（天地图开放平台密钥）以显示地图。'
        })}
      </div>
    );
  }

  return (
    <div
      id={mapDomIdRef.current}
      ref={containerRef}
      className="tianditu-map-container"
      style={{ width: '100%', height: '100%', minHeight: 360 }}
    />
  );
};

export default withTranslation()(CustomizeTiandituMap);
